import { useEffect, useRef, useState } from 'react';
import {
  PaperclipIcon,
  CaretDownIcon,
  CaretUpIcon,
  CircleNotchIcon,
  TranslateIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useEmailThread } from '@/api/communications/email-threads/useEmailThread';
import { useTranslateInboundEmail } from '@/api/communications/inbound-emails/useTranslateInboundEmail';
import { useActivityGraphContext } from '../ActivityGraphContext';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import {
  renderEmailBody,
  buildCidMap,
} from '@/components/email-viewer/email-thread-utils';
import { cn } from '@/lib/utils';
import { getLanguageDisplayName } from '@/lib/language-utils';
import type { TranslatedEmailContent } from '@erp/rpc';

function getTranslation(
  translatedTextContent:
    | Record<string, string | TranslatedEmailContent>
    | null
    | undefined,
  lang: string,
): TranslatedEmailContent | null {
  if (!translatedTextContent) return null;
  const translation = translatedTextContent[lang];
  if (!translation) return null;

  if (typeof translation === 'string') {
    return { body: translation, isHtml: false };
  }

  return translation;
}

interface EmailsTabProps {
  conversationId?: string | null;
}

export function EmailsTab({ conversationId }: EmailsTabProps) {
  const context = useActivityGraphContext();
  const {
    data: threadData,
    isLoading,
    error,
  } = useEmailThread(conversationId ?? null);

  const { enabled: isActivityLogEnabled } = useFeatureFlag(
    'activity-log-enabled',
    false,
  );
  const emailRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const didAutoExpandLatestEmailRef = useRef(false);

  const translateMutation = useTranslateInboundEmail(conversationId ?? null);
  const [showOriginalByEmailId, setShowOriginalByEmailId] = useState<
    Record<string, boolean>
  >({});
  const [lastTranslateAttemptEmailId, setLastTranslateAttemptEmailId] =
    useState<string | null>(null);

  const expandedEmailIds = context?.expandedEmailIds;
  const highlightedEmailId = context?.highlightedEmailId ?? null;

  useEffect(() => {
    didAutoExpandLatestEmailRef.current = false;
  }, [conversationId]);

  useEffect(() => {
    if (
      didAutoExpandLatestEmailRef.current ||
      !threadData?.emails?.length ||
      !context ||
      !expandedEmailIds
    ) {
      return;
    }

    const latestEmail = threadData.emails[threadData.emails.length - 1];
    if (expandedEmailIds.size === 0) {
      didAutoExpandLatestEmailRef.current = true;
      context.toggleEmailExpanded(latestEmail.id);
    }
  }, [threadData?.emails, expandedEmailIds, context]);

  // Scroll to highlighted email
  useEffect(() => {
    if (highlightedEmailId && emailRefs.current.has(highlightedEmailId)) {
      const element = emailRefs.current.get(highlightedEmailId);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const timer = setTimeout(() => {
        context?.clearHighlights();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [highlightedEmailId, context]);

  const handleToggleExpanded = (emailId: string) => {
    context?.toggleEmailExpanded(emailId);
  };

  const handleExpandAll = () => {
    if (threadData?.emails && context) {
      context.expandAllEmails(threadData.emails.map((e) => e.id));
    }
  };

  const handleCollapseAll = () => {
    context?.collapseAllEmails();
  };

  const handleAttachmentClick = (documentId: string) => {
    context?.viewDocument(documentId);
  };

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        No email conversation linked
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        Error loading emails
      </div>
    );
  }

  if (!threadData?.emails?.length) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        No emails found
      </div>
    );
  }

  const participantEmails = threadData.participantEmails?.join(', ') || '';

  const isInlineImageDocument = (document: {
    contentId?: string | null;
    mimeType?: string | null;
    originalFileName?: string | null;
    name?: string | null;
  }) => {
    if (!document.contentId) return false;
    if (document.mimeType?.startsWith('image/')) return true;

    const fileName = (
      document.originalFileName ||
      document.name ||
      ''
    ).toLowerCase();
    return (
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.gif') ||
      fileName.endsWith('.webp') ||
      fileName.endsWith('.svg')
    );
  };

  return (
    <div className="p-3 space-y-3">
      {/* Header */}
      {!isActivityLogEnabled && (
        <div className="space-y-2">
          {participantEmails && (
            <div className="space-y-1">
              <span className="text-sm font-medium text-muted-foreground">
                Participants
              </span>
              <p className="text-sm truncate">{participantEmails}</p>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExpandAll}
            >
              Expand All
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCollapseAll}
            >
              Collapse All
            </Button>
          </div>
        </div>
      )}

      {/* Email list */}
      <div className="space-y-2">
        {threadData.emails.map((email) => {
          const isExpanded = expandedEmailIds?.has(email.id);
          const isHighlighted = highlightedEmailId === email.id;
          const attachmentCount =
            (email.attachments?.length ?? 0) +
            (email.documentAssociations?.length ?? 0);

          const normalizedDetectedLanguage = email.detectedLanguage
            ?.trim()
            .toLowerCase();
          const isOutboundEmail = email.service === 'microsoft-graph-outbound';
          const translation = getTranslation(email.translatedTextContent, 'en');
          const hasEnglishTranslation = Boolean(
            translation?.body || translation?.subject,
          );
          const canTranslateToEnglish =
            !isOutboundEmail &&
            normalizedDetectedLanguage &&
            normalizedDetectedLanguage !== 'en' &&
            !hasEnglishTranslation;
          const shouldShowTranslationBar =
            !isOutboundEmail &&
            normalizedDetectedLanguage &&
            normalizedDetectedLanguage !== 'en';
          const showOriginal = Boolean(showOriginalByEmailId[email.id]);

          return (
            <div
              key={email.id}
              ref={(el) => {
                if (el) emailRefs.current.set(email.id, el);
              }}
              className={cn(
                'rounded-md border transition-all',
                isHighlighted
                  ? 'border-primary border-2 bg-primary/5'
                  : 'border-border/50 bg-muted/30',
              )}
            >
              {!isExpanded ? (
                // Collapsed view
                <div className="p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-sm truncate flex items-center gap-1">
                      {hasEnglishTranslation && translation?.subject ? (
                        <>
                          {translation.subject}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <TranslateIcon className="h-3 w-3 text-muted-foreground shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Translated from{' '}
                              {getLanguageDisplayName(
                                normalizedDetectedLanguage,
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </>
                      ) : (
                        email.subject || 'No Subject'
                      )}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">
                        {new Date(email.createdAt).toLocaleDateString([], {
                          month: 'short',
                          day: 'numeric',
                        })}{' '}
                        {new Date(email.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleExpanded(email.id);
                        }}
                      >
                        <CaretDownIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span className="truncate">{email.from}</span>
                    {attachmentCount > 0 && (
                      <>
                        <span>-</span>
                        <div className="flex items-center gap-1">
                          <PaperclipIcon size={10} />
                          <span>{attachmentCount}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                // Expanded view
                <div className="p-3 space-y-2">
                  {/* Header */}
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-semibold text-sm flex-1 flex items-center gap-1">
                        {hasEnglishTranslation &&
                        !showOriginal &&
                        translation?.subject ? (
                          <>
                            {translation.subject}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <TranslateIcon className="h-3 w-3 text-muted-foreground shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Translated from{' '}
                                {getLanguageDisplayName(
                                  normalizedDetectedLanguage,
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </>
                        ) : (
                          email.subject || 'No Subject'
                        )}
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleExpanded(email.id);
                        }}
                      >
                        <CaretUpIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        <div>
                          <span className="font-medium">From:</span>{' '}
                          {email.from}
                        </div>
                        <div>
                          <span className="font-medium">To:</span> {email.to}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {new Date(email.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="pt-2 border-t">
                    {shouldShowTranslationBar ? (
                      <div className="flex flex-col gap-1 mb-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs text-muted-foreground">
                            {normalizedDetectedLanguage && hasEnglishTranslation
                              ? 'Translated'
                              : null}
                          </div>

                          {!hasEnglishTranslation ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={translateMutation.isPending}
                              onClick={() => {
                                setLastTranslateAttemptEmailId(email.id);
                                translateMutation.mutate({
                                  inboundEmailId: email.id,
                                  targetLanguage: 'en',
                                });
                              }}
                            >
                              <TranslateIcon className="h-3 w-3 mr-1" />
                              {translateMutation.isPending &&
                              lastTranslateAttemptEmailId === email.id
                                ? 'Translating...'
                                : 'Translate to English'}
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowOriginalByEmailId((prev) => ({
                                  ...prev,
                                  [email.id]: !prev[email.id],
                                }));
                              }}
                            >
                              {showOriginal
                                ? 'See translation'
                                : 'See original'}
                            </Button>
                          )}
                        </div>

                        {translateMutation.error &&
                        lastTranslateAttemptEmailId === email.id ? (
                          <div className="text-xs text-destructive">
                            Translation failed. Please try again.
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {hasEnglishTranslation &&
                    !showOriginal &&
                    translation?.body ? (
                      <div className="prose prose-sm max-w-none">
                        {translation.isHtml ? (
                          renderEmailBody(
                            translation.body,
                            buildCidMap(email.documentAssociations),
                          )
                        ) : (
                          <pre className="whitespace-pre-wrap text-sm leading-relaxed">
                            {translation.body}
                          </pre>
                        )}
                      </div>
                    ) : (
                      <div className="prose prose-sm max-w-none">
                        {renderEmailBody(
                          email.messageBodyHtml ||
                            email.htmlContent ||
                            email.textContent,
                          buildCidMap(email.documentAssociations),
                        )}
                      </div>
                    )}
                  </div>

                  {/* Attachments */}
                  {attachmentCount > 0 && (
                    <div className="pt-2 border-t">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
                        <PaperclipIcon size={12} />
                        <span>
                          {attachmentCount} attachment
                          {attachmentCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {/* Document associations */}
                        {email.documentAssociations?.map((assoc) => {
                          if (isInlineImageDocument(assoc.document))
                            return null;
                          return (
                            <button
                              key={assoc.id}
                              type="button"
                              className="flex items-center gap-2 w-full text-left text-sm p-2 rounded hover:bg-muted/50 transition-colors"
                              onClick={() =>
                                handleAttachmentClick(assoc.document.id)
                              }
                            >
                              <PaperclipIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="truncate">
                                {assoc.document.originalFileName ||
                                  assoc.document.name}
                              </span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {assoc.document.fileSizeBytes
                                  ? `${(assoc.document.fileSizeBytes / 1024).toFixed(1)} KB`
                                  : ''}
                              </span>
                            </button>
                          );
                        })}
                        {/* Legacy attachments */}
                        {email.attachments?.map((att) => (
                          <button
                            key={att.id}
                            type="button"
                            className="flex items-center gap-2 w-full text-left text-sm p-2 rounded hover:bg-muted/50 transition-colors"
                            onClick={() => handleAttachmentClick(att.id)}
                          >
                            <PaperclipIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="truncate">{att.fileName}</span>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {(att.sizeBytes / 1024).toFixed(1)} KB
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
